import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { HotelResult, RoomRate } from '@niarab2c/frontend-commons/src/types/hotels';
import _isEqual from 'lodash/isEqual';
import { nanoid } from 'nanoid/non-secure';
import { ShallowClient } from 'niara-spear-crudmodel';
import type { CompleteRootState, CriteriaForm } from '../base';
import * as HotelSearch from './hotel/hotelSearch';

/**
 * Criteria usado no niara-spear
 */
export type NiaraCriteria = {
  time: {
    startDate: string;
    endDate: string;
  };
  occupancy: {
    adults: number;
    children?: number;
    childrenAges?: number[];
    quantity: number;
  };
  tags?: Array<string>;
  destinations?: {
    cityIds?: Array<string>;
    hotelIds?: Array<string>;
    propertyId?: string;
    contentType?: string;
    position?: {
      latitude: number;
      longitude: number;
    };
  };
  landingPage?: string;
  clientId: string;
  clientName?: string;
  searchId?: string;
  selfBooking?: boolean;
  selfBookingTraveller?: boolean;
  b2c?: boolean;
  bestOnly?: boolean;
  travellerId?: string;
  promoCode?: string;
  destinationName?: string;
  propertyId?: string;
  contentType?: string;
  descriptions?: boolean;
  credentialIds?: string[];
  paymentTypes?: string[];
  modification?: {
    reservationIdOnSource;
    roomIdOnSource;
  };
  currencyCode?;
  splitRooms?: boolean;
  availRatesOnly?: boolean;
  distributionIds?: string[];
  roomRateIds?: string[];
  limit?: number;
  offset?: number;
  propertyCnt?: number;
};
export type MailableResult = {
  id: string;
  label: string;
  result: Pick<HotelResult, 'hotel' | '_id'>;
  roomRates: [RoomRate];
  engineRuleVersion: '0' | '1';
  criteria: NiaraCriteria;
  person?: {
    personName: string;
    personId: string;
  };
};
export type QuotationRoomRatesState = {
  selectedRoomRates: MailableResult[];
  openModal?: boolean;
  searchId?: string;
  client?: ShallowClient;
};
const initialState: QuotationRoomRatesState = {
  selectedRoomRates: []
};
export const rehydrateAndAdd = createAsyncThunk<{
  result: MailableResult;
  searchId: string;
}, {
  result: MailableResult;
  openModal?: boolean;
}, {
  state: CompleteRootState;
}>('quotationRoomRates/rehydrateAndAdd', async (args, {
  dispatch,
  getState
}) => {
  const mailableResult = args.result;
  const hotelResult = mailableResult.result;
  const roomRate = mailableResult.roomRates[0];
  const locale = getState().quotationRoomRates?.client?.systemLocale;
  const searchId = getState().quotationRoomRates?.searchId;
  if (roomRate.locale && locale && roomRate.locale != locale) {
    const criteriaForm: CriteriaForm = {
      availRatesOnly: false,
      clientId: mailableResult.criteria.clientId,
      clientName: mailableResult.criteria.clientName,
      startDate: mailableResult.criteria.time.startDate,
      endDate: mailableResult.criteria.time.endDate,
      rooms: [roomRate.occupancy],
      roomRateIds: [roomRate.id],
      roomTypeId: roomRate.roomType.id?.toString(),
      promoCode: roomRate.promoCode
      // não incluir roomRateId, para não quebrar o metabuscador
    };
    dispatch(HotelSearch.hydrateHotelResults({
      results: [{
        ...hotelResult,
        _hydrationCompleted: false,
        _hydrationInProgress: false
      }],
      engineRuleVersion: mailableResult.engineRuleVersion,
      criteriaForm,
      searchId,
      locale
    }));
  }
  return {
    result: mailableResult,
    searchId
  };
});
export const quotationRoomRatesSlice = createSlice({
  name: 'quotationRoomRates',
  initialState,
  reducers: {
    add: (state, action: PayloadAction<MailableResult>) => {
      const roomRate = state.selectedRoomRates?.find(r => r.id === action.payload.id);
      if (!roomRate) {
        const engineRuleVersion = action.payload.roomRates[0].engineRuleVersion;
        const selectedResultsForMail = (state.selectedRoomRates ?? []).concat(action.payload).filter(
        // não deixar misturar tarifas de engineRuleVersion diferentes
        r => r.roomRates[0].engineRuleVersion == engineRuleVersion);
        state.selectedRoomRates = selectedResultsForMail;
        state.openModal = undefined;
      }
    },
    remove: (state, action: PayloadAction<MailableResult>) => {
      const roomRates = state.selectedRoomRates?.filter(r => r.id !== action.payload.id);
      state.selectedRoomRates = roomRates;
      state.openModal = undefined;
    },
    updateSelectedRoomRates: (state, action: PayloadAction<MailableResult[]>) => {
      if (!_isEqual(state.selectedRoomRates, action.payload)) {
        state.selectedRoomRates = action.payload;
        state.openModal = undefined;
      }
    },
    updateOpenModal: (state, action: PayloadAction<boolean | undefined>) => {
      state.openModal = action.payload;
    },
    setClient: (state, action: PayloadAction<{
      client: ShallowClient;
    }>) => {
      const client = action.payload.client;
      if (client && client.id != state.client?.id) {
        state.client = client;
        state.selectedRoomRates = [];
      }
    }
  },
  extraReducers: builder => {
    builder.addCase(rehydrateAndAdd.pending, (state, action) => {
      const mailableResult = action.meta.arg.result;
      const roomRate = state.selectedRoomRates?.find(r => r.id === mailableResult.id);
      if (!roomRate) {
        const engineRuleVersion = mailableResult.roomRates[0].engineRuleVersion;
        const selectedResultsForMail = (state.selectedRoomRates ?? []).concat(mailableResult).filter(
        // não deixar misturar tarifas de engineRuleVersion diferentes
        r => r.roomRates[0].engineRuleVersion == engineRuleVersion);
        state.selectedRoomRates = selectedResultsForMail;
      }
      if (action.meta.arg.openModal != null) {
        state.openModal = action.meta.arg.openModal;
      }
      state.searchId = state.searchId ?? nanoid();
      return state;
    });
    builder.addCase(rehydrateAndAdd.fulfilled, (state, action) => {
      state.searchId = action.payload.searchId;
      return state;
    });
    builder.addCase(HotelSearch.hydrateHotelResults.fulfilled, (state, action) => {
      if (action.payload.searchId == state.searchId) {
        const roomRate = action.payload.results?.[0]?.roomRates?.[0];
        if (roomRate) {
          const stateSelectedRoomRate = state.selectedRoomRates.find(r => r.roomRates?.[0].id == roomRate.id);
          if (stateSelectedRoomRate) {
            stateSelectedRoomRate.roomRates = [roomRate];
          }
        }
      }
    });
  }
});
export const {
  add,
  remove,
  updateSelectedRoomRates,
  updateOpenModal,
  setClient
} = quotationRoomRatesSlice.actions;
export default quotationRoomRatesSlice.reducer;